import React, { useEffect, useState } from "react";
import { Redirect, RouteComponentProps } from "react-router-dom";
import { CircularProgress, Grid, makeStyles, Theme, Typography } from "@material-ui/core";

import { PremiumSermonSeriesService } from "../../services/PremiumSermonSeriesService";
import { ServerResponse } from "../../services/server/OldWebClient";
import { PremiumSermonService } from "../../services/PremiumSermonService";
import { Search as PremiumSermonSearch } from "../../utilities/PremiumSermonSearch";
import { Search as PremiumSermonSeriesSearch } from "../../utilities/PremiumSeriesSearch";
import { getParameterByName } from "../../utilities/UrlInteraction";
import { MarketingPageType } from "./types/MarketingPageType";
import { ApplicationUser } from "../../entities/ApplicationUser";
import { PremiumSermon } from "../../entities/PremiumSermon";
import { PremiumSermonSeries } from "../../entities/PremiumSermonSeries";
import { SermonCard } from "./components/sermonCard";
import { Hero } from "./components/hero";
import { ServerFailed } from "../errors/serverFailed";
import { orderBy } from "../../utilities/orderBy";
import { useUser } from "../../utilities/useUser";
import { routes } from "../../routes";

const useStyles = makeStyles((theme: Theme) => ({
	hideOverflow: {
		overflow: "hidden",
	},
	loadingContainer: {
		marginLeft: "auto",
		marginRight: "auto",
		display: "flex",
	},
	loading: {
		marginTop: theme.spacing(4),
		marginBottom: theme.spacing(1),
	},
	tileContainer: {
		padding: "25px 10px 30px 10px",
		maxWidth: "1080px",
		marginLeft: "auto",
		marginRight: "auto",
	},
	noResults: {
		marginLeft: "auto",
		marginRight: "auto",
		marginTop: "auto",
		fontWeight: 400,
		fontSize: "1.5em",
	},
}));

interface Props {
	pageType: MarketingPageType;
}

export function ProductsList({ pageType }: Props) {
	const classes = useStyles();
	const userContext = useUser();
	const user = userContext.onlineUser;

	const [premiumSermonSeries, setPremiumSermonSeries] = useState<PremiumSermonSeries[]>();
	const [premiumSermons, setPremiumSermons] = useState<PremiumSermon[]>();
	const [queryString, setQueryString] = useState<string | null>(null);
	const [serverError, setServerError] = useState<boolean>(false);
	const seriesRedirectLink = "/premium-series";
	const sermonRedirectLink = "/premium-sermons";

	const heroProps = {
		assetName: `Popular ${pageType === MarketingPageType.Series ? "Sermon Series" : "Sermons"}`,
		headerPreText: "CONFIDENT PREACHING",
		assetShortDescription: undefined,
		buttonText: "GET STARTED FREE",
		buttonSubText: `Unlock all premium ${pageType} FREE with Sermon Maker`,
	};

	const getPremiumSeriesData = async () => {
		let response = await PremiumSermonSeriesService.getAllEnabled();
		if (ServerResponse.isSuccess(response)) {
			setServerError(false);
			setPremiumSermonSeries(response.data);
		} else {
			setServerError(true);
		}
	};

	const getPremiumSermonsData = async () => {
		let response = await PremiumSermonService.getAllEnabled();
		if (ServerResponse.isSuccess(response)) {
			setServerError(false);
			setPremiumSermons(response.data);
		} else {
			setServerError(true);
		}
	};

	const renderLoadingIcon = () => {
		return (
			<div className={classes.loadingContainer}>
				<CircularProgress className={classes.loading} size={35} />
			</div>
		);
	};

	const renderPremiumSermonSeriesTiles = (premiumSermonSeries: PremiumSermonSeries[], queryString: string | null = null) => {
		if (queryString !== null) {
			premiumSermonSeries = PremiumSermonSeriesSearch(queryString, premiumSermonSeries);
		}

		if (premiumSermonSeries.length < 1) {
			return <Typography className={classes.noResults}>No results found for query: {queryString}</Typography>;
		}

		let sortedSeries = premiumSermonSeries.sort(orderBy.date((series) => series.dateCreated, "Descending"));
		return sortedSeries.map((series) => {
			return (
				<SermonCard
					key={series.id}
					assetName={series.name}
					assetType={MarketingPageType.Series}
					cardTitle={series.name}
					cardContent={series.shortDescription}
					cardImage={series.thumbnailUrl}
				/>
			);
		});
	};

	const renderPremiumSermonsTiles = (premiumSermons: PremiumSermon[], queryString: string | null = null) => {
		if (queryString !== null) {
			premiumSermons = PremiumSermonSearch(queryString, premiumSermons);
		}

		if (premiumSermons.length < 1) {
			return <Typography className={classes.noResults}>No results found for query: {queryString}</Typography>;
		}

		let sortedSermons = premiumSermons.sort(orderBy.date((sermon) => sermon.dateCreated, "Descending"));
		return sortedSermons.map((sermon) => {
			return (
				<SermonCard
					key={sermon.id}
					assetName={sermon.name}
					assetType={MarketingPageType.Sermons}
					cardTitle={sermon.name}
					cardContent={sermon.shortDescription}
					cardImage={sermon.thumbnailUrl}
				/>
			);
		});
	};

	const renderContent = () => {
		if (premiumSermonSeries !== undefined && pageType === MarketingPageType.Series) {
			return renderPremiumSermonSeriesTiles(premiumSermonSeries, queryString);
		}

		if (premiumSermons !== undefined && pageType === MarketingPageType.Sermons) {
			return renderPremiumSermonsTiles(premiumSermons, queryString);
		}

		return renderLoadingIcon();
	};

	useEffect(() => {
		if (pageType === MarketingPageType.Series) {
			getPremiumSeriesData();
		} else if (pageType === MarketingPageType.Sermons) {
			getPremiumSermonsData();
		}

		if (window.location.search !== "") {
			let query = getParameterByName("query");
			setQueryString(query);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			{user === null || user?.makerSubscription === null ? (
				<Grid container className={classes.hideOverflow}>
					<Hero {...heroProps} callToActionUrl={user ? routes.app.activatePage : routes.public.reqisterPage} />
					<Grid container spacing={2} className={classes.tileContainer}>
						{!serverError && renderContent()}
						{serverError && <ServerFailed />}
					</Grid>
				</Grid>
			) : (
				<Redirect to={pageType === MarketingPageType.Series ? seriesRedirectLink : sermonRedirectLink} />
			)}
		</>
	);
}